import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthContext.js";
import {
  useEntries,
  usePairing,
  useTodaySharedByPartner,
  type EntryFilter,
} from "../api/queries.js";
import { groupByDay, daysTogether } from "../lib/format.js";
import { NavBar } from "../components/NavBar.js";
import { Avatar } from "../components/Avatar.js";
import { Empty } from "../components/Empty.js";
import { EntryCard } from "../components/EntryCard.js";
import { Loading, QueryError } from "../components/AsyncState.js";
import { useToast } from "../components/ToastContext.js";
import {
  ChevronRightIcon,
  LockIcon,
  PencilIcon,
  RecallIcon,
} from "../components/icons.js";

const FILTERS: { id: EntryFilter; label: string }[] = [
  { id: "all", label: "全部" },
  { id: "theirs", label: "TA 的日记" },
  { id: "mine", label: "我的" },
];

/**
 * 首页（spec §5.4.1）。
 *
 * 三档筛选在数据层就是三条不同的查询，不是对同一份数组做三种 filter：
 * 后端 `filter` 已经按可见性推导过，「全部」= 我的全部（含私密）+ TA 的共享。
 *
 * 「TA 今天分享了一篇日记」只由**对方共享**的篇目推导 —— 查询的是
 * `filter=theirs`，本人新增私密日记不会让它亮起来（spec §4.3）。
 */
export function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [filter, setFilter] = useState<EntryFilter>("all");

  const pairing = usePairing();
  const list = useEntries(filter);
  // 配对卡专用；`filter === "theirs"` 时与 list 共用同一份缓存，不会多发请求。
  const theirs = useEntries("theirs");
  const sharedToday = useTodaySharedByPartner(theirs.data?.entries);

  if (user === null) return null;

  const active = pairing.data?.state === "active";
  const partner = pairing.data?.partnerUsername ?? "TA";
  const days = daysTogether(pairing.data?.pairedAt);

  const groups = groupByDay(list.data?.entries ?? []);

  return (
    <>
      <NavBar
        title="我们的日记"
        left={<Avatar name={user.username} variant="me" size={28} />}
        right={
          <button
            type="button"
            className="icon-btn"
            onClick={() => navigate("/entries/new")}
            aria-label="写日记"
          >
            <PencilIcon size={19} />
          </button>
        }
      />

      <div className="page">
        {/* 配对状态卡 */}
        {pairing.data === undefined ? (
          <div className="pair-card">
            <div className="pair-info">
              <span className="l1">…</span>
              <span className="l2">正在读取配对状态</span>
            </div>
          </div>
        ) : active ? (
          <div className="pair-card">
            <div className="dual-ava">
              <Avatar name={user.username} variant="me" size={36} />
              <Avatar name={partner} variant="ta" size={36} />
            </div>
            <div className="pair-info">
              <span className="l1">
                {partner}
                {days !== null ? ` · 已配对 ${days} 天` : ""}
              </span>
              <span className="l2">
                {sharedToday ? "TA 今天分享了一篇日记" : "双向授权中"}
              </span>
            </div>
            <button
              type="button"
              className="circle-btn"
              onClick={() => navigate("/entries/new")}
              aria-label="写日记"
            >
              <PencilIcon size={17} style={{ color: "#fff" }} />
            </button>
          </div>
        ) : (
          <div className="pair-card">
            <div className="pair-info">
              <span className="l1">还没有配对</span>
              <span className="l2">配对之后才能读到彼此共享的日记</span>
            </div>
            <Link
              to="/pairing"
              className="chip"
              style={{
                color: "#fff",
                borderColor: "var(--stone)",
                background: "var(--primary)",
                height: 34,
                padding: "0 16px",
                fontWeight: 600,
              }}
            >
              去配对
            </Link>
          </div>
        )}

        {/* 回忆入口 —— spec §7.3 未定形态，这里只是占位，不做假页面 */}
        <button
          type="button"
          className="recall-entry"
          onClick={() => toast.show("「回忆」还在路上 —— 现在是一处占位入口")}
        >
          <RecallIcon size={18} style={{ color: "var(--brand-purple-800)" }} />
          <span className="re-txt">
            <span className="t">回忆</span>
            <span className="d">看看过去的今天</span>
          </span>
          <ChevronRightIcon size={16} style={{ color: "var(--stone)" }} />
        </button>

        <div className="chip-row">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={filter === f.id ? "chip on" : "chip"}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {list.isLoading && <Loading />}
        {list.isError && (
          <QueryError error={list.error} onRetry={() => void list.refetch()} />
        )}

        {list.data !== undefined && groups.length === 0 && (
          <Empty
            icon={
              filter === "theirs" ? (
                <LockIcon size={22} style={{ color: "var(--steel)" }} />
              ) : (
                <PencilIcon size={22} style={{ color: "var(--steel)" }} />
              )
            }
            title={
              filter === "theirs"
                ? "TA 还没有分享日记"
                : filter === "mine"
                  ? "你还没有写过日记"
                  : "这里还很安静"
            }
            description={
              filter === "theirs"
                ? "对方主动分享的日记会出现在这里。TA 写给自己看的东西不会显示。"
                : "写下的日记可以选择只给自己看，也可以分享给 TA。"
            }
            action={
              filter !== "theirs" ? (
                <Link to="/entries/new" className="btn btn-primary btn-sm">
                  写第一篇
                </Link>
              ) : undefined
            }
          />
        )}

        {groups.map((group) => (
          <div key={group.key} className="stack-sm">
            <div className="day-label">{group.label}</div>
            {group.items.map((entry) => (
              <EntryCard key={entry.id} entry={entry} meId={user.id} />
            ))}
          </div>
        ))}
      </div>
    </>
  );
}
